<template>
  <div class="container-fluid py-4">

    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-person-plus fs-2 text-primary"></i>
        <h2 class="fw-bold mb-0">Création d'un Patient</h2>
      </div>
      <router-link class="btn btn-primary btn-lg shadow-sm btn-clinique" :to="{ name: 'patients' }">
        <i class="bi bi-arrow-left-circle me-2"></i> Retour
      </router-link>
    </div>

    <!-- Card Formulaire -->
    <div class="card p-4 shadow-sm rounded card-clinique">
      <div class="row g-4">
        <!-- Nom -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Nom</label>
          <input type="text" v-model="patient.nom" placeholder="nom" class="form-control form-control-clinique" :class="{ 'is-invalid': error.nom }">
          <div class="invalid-feedback">{{ error.nom }}</div>
        </div>

        <!-- Prénom -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Prénom</label>
          <input type="text" v-model="patient.prenom" placeholder="prénom" class="form-control form-control-clinique" :class="{ 'is-invalid': error.prenom }">
          <div class="invalid-feedback">{{ error.prenom }}</div>
        </div>

        <!-- Dossier qui existe peut-être déjà (avertissement seulement) -->
        <div class="col-12" v-if="patient.nom">
          <AlerteDoublons :nom="patient.nom" :prenom="patient.prenom" :date-naissance="patient.dateNaissance" />
        </div>

        <!-- Genre -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Genre</label>
          <select v-model="patient.genre" class="form-select form-select-clinique" :class="{ 'is-invalid': error.genre }">
            <option value="" disabled>-- Sélectionnez --</option>
            <option value="Masculin">Masculin</option>
            <option value="Feminin">Feminin</option>
          </select>
          <div class="invalid-feedback">{{ error.genre }}</div>
        </div>

        <!-- Date de naissance, ou âge estimé -->
        <div class="col-md-6">
          <AgeNaissance v-model:date-naissance="patient.dateNaissance" v-model:estimee="patient.dateNaissanceEstimee" />
        </div>

        <!-- Profession -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Profession</label>
          <input type="text" v-model="patient.profession" placeholder="profession" class="form-control form-control-clinique">
        </div>

        <!-- Nombre d'enfants -->
       

        <!-- Téléphone -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Téléphone <small class="text-muted fw-normal" v-if="mineur">(du patient, s'il en a un)</small></label>
          <input type="tel" v-model="patient.telephone" placeholder="téléphone" class="form-control form-control-clinique" :class="{ 'is-invalid': error.telephone }">
          <div class="invalid-feedback">{{ error.telephone }}</div>
        </div>

        <!-- Email -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Email</label>
          <input type="email" v-model="patient.email" placeholder="email" class="form-control form-control-clinique" :class="{ 'is-invalid': error.email }">
          <div class="invalid-feedback">{{ error.email }}</div>
        </div>
        <!-- Personne à contacter en cas d'urgence (facultatif) -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Personne à contacter (en cas d’urgence)</label>
          <div class="input-group">
            <input type="text" v-model="patient.contactUrgenceNom" placeholder="Nom (facultatif)" class="form-control form-control-clinique">
            <input type="tel" v-model="patient.contactUrgenceTelephone" placeholder="Téléphone" class="form-control form-control-clinique" :class="{ 'is-invalid': error.contactUrgenceTelephone }">
          </div>
          <div class="text-danger small" v-if="error.contactUrgenceTelephone">{{ error.contactUrgenceTelephone }}</div>
        </div>

        <!-- Responsables légaux (enfant, nouveau-né, personne sous tutelle) -->
        <div class="col-12">
          <ResponsablesPatient v-model="responsables" :mineur="mineur" :nom-patient="patient.nom" />
          <div class="text-danger small" v-if="error.responsables">{{ error.responsables }}</div>
        </div>

        <!-- À qui envoyer les SMS -->
        <div class="col-md-6" v-if="mineur || responsables.length">
          <label class="form-label fw-semibold">SMS envoyés à</label>
          <select v-model="patient.smsDestinataire" class="form-select form-select-clinique">
            <option v-for="o in destinatairesSms" :key="o.valeur" :value="o.valeur">{{ o.libelle }}</option>
          </select>
        </div>

        <!-- Adresse -->
        <div class="col-md-12">
          <label class="form-label fw-semibold">Adresse</label>
          <input type="text" v-model="patient.adresse" placeholder="adresse" class="form-control form-control-clinique">
        </div>

        <!-- Groupe Sanguin -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Groupe sanguin</label>
          <select v-model="patient.groupeSanguin" class="form-select form-select-clinique">
            <option value="">-- Sélectionnez --</option>
            <option>O+</option><option>O-</option>
            <option>A+</option><option>A-</option>
            <option>B+</option><option>B-</option>
            <option>AB+</option><option>AB-</option>
          </select>
        </div>

        <!-- Maladies particulières -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Antécédents médicaux</label>
          <textarea v-model="patient.antecedentsMedicaux" class="form-control form-control-clinique" rows="2" placeholder="Ex: Diabète, hypertension..."></textarea>
        </div>

        <!-- Allergies -->
        <div class="col-md-6">
          <label class="form-label fw-semibold">Allergies</label>
          <textarea v-model="patient.allergies" placeholder="allergie" class="form-control form-control-clinique" rows="2"></textarea>
        </div>

        <!-- Assurance : le contrat se saisit juste après la création -->
        <div class="col-md-6 d-flex align-items-center" v-if="$store.getters.hasModule('ASSURANCES')">
          <div class="form-check form-switch">
            <input id="assure" v-model="estAssure" class="form-check-input" type="checkbox">
            <label class="form-check-label fw-semibold" for="assure">Le patient a une assurance</label>
          </div>
          <small class="text-muted ms-2">Le contrat (assureur, n° d'assuré, taux) se saisit à l'étape suivante.</small>
        </div>

        <!-- Ancienne saisie libre de l'assurance (remplacée par les contrats)
        <div class="col-12 mt-3">
          <h5 class="fw-bold text-primary">Informations Assurance</h5>
        </div>

        <div class="col-md-4">
          <label class="form-label fw-semibold">Nom de l'assurance</label>
          <input type="text" v-model="patient.assuranceNom" placeholder="nom de l'assurance" class="form-control form-control-clinique">
        </div>

        <div class="col-md-4">
          <label class="form-label fw-semibold">Numéro d’assurance</label>
          <input type="text" v-model="patient.assuranceNumero" placeholder="numéro de l'assurance" class="form-control form-control-clinique">
        </div>

        <div class="col-md-4">
          <label class="form-label fw-semibold">Contact assureur</label>
          <input type="text" v-model="patient.assureurContact" placeholder="contact assureur" class="form-control form-control-clinique">
        </div>-->

        <!-- Statut -->
        <div class="col-md-6 d-flex align-items-center mt-3">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" v-model="patient.isEnabled">
            <label class="form-check-label ms-2 fw-semibold">Activé ?</label>
          </div>
        </div>

      </div>

      <!-- Notice d'information (loi n°2013-015, art. 15) -->
      <div class="border rounded p-3 mt-4 bg-light d-flex align-items-center gap-3 flex-wrap">
        <div class="form-check mb-0">
          <input id="noticeRemise" v-model="patient.noticeRemise" type="checkbox" class="form-check-input">
          <label for="noticeRemise" class="form-check-label">Le patient a été informé de l'utilisation de ses données (notice remise ou lue)</label>
        </div>
        <a href="#" class="small ms-auto" @click.prevent="imprimerNotice"><i class="bi bi-printer me-1"></i>Imprimer la notice</a>
      </div>

      <!-- Boutons -->
      <div class="d-flex justify-content-end gap-3 mt-4">
        <button class="btn btn-light border shadow-sm px-4 py-2" @click="cancel">
          <i class="bi bi-x-circle me-1"></i> Annuler
        </button>
        <button class="btn btn-success shadow px-4 py-2 btn-clinique" :disabled="isLoading" @click="savePatient">
          <i class="bi bi-save me-1"></i>
          <span v-if="isLoading">Chargement...</span>
          <span v-else>Enregistrer</span>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { telephoneValide, emailValide, MESSAGE_TELEPHONE, MESSAGE_EMAIL } from '@/utils/validation';
import api, { messageErreur, notifier } from '@/api';
import AgeNaissance from '@/components/patient/AgeNaissance.vue';
import AlerteDoublons from '@/components/patient/AlerteDoublons.vue';
import ResponsablesPatient, { lignesPourServeur, lignesValides } from '@/components/patient/ResponsablesPatient.vue';
import { estMineur, DESTINATAIRES_SMS } from '@/utils/age';
import { imprimer, configClinique } from '@/impression/impression';
import { documentNotice } from '@/impression/documents/notice';

export default {
  components: { AgeNaissance, AlerteDoublons, ResponsablesPatient },
  data() {
    return {
      isLoading: false,
      responsables: [],
      destinatairesSms: DESTINATAIRES_SMS,
      patient: {
        nom: '',
        prenom: '',
        genre: '',
        dateNaissance: '',
        dateNaissanceEstimee: false,
        telephone: '',
        contactUrgenceTelephone:'',
        contactUrgenceNom: '',
        smsDestinataire: 'AUTO',
        email: '',
        adresse: '',
        profession: '',
        groupeSanguin: '',
        antecedentsMedicaux: '',
        allergies: '',
        assuranceNom: '',
        assuranceNumero: '',
        assureurContact: '',
        isEnabled: true,
        noticeRemise: false,
        createdBy: null
      },
      estAssure: false,
      error: {
        nom: '',
        prenom: '',
        genre: '',
        //telephone: '',
        //contactUrgenceTelephone:'',
      }
    }
  },

  computed: {
    currentUser() {
      return this.$store.getters.user;
    },
    mineur() {
      return estMineur(this.patient.dateNaissance) === true;
    }
  },

  methods: {
    validateForm() {
      this.error = {
        nom: this.patient.nom ? '' : 'Le nom est requis',
        prenom: this.patient.prenom ? '' : 'Le prénom est requis',
        genre: this.patient.genre ? '' : 'Le genre est requis',
        telephone: telephoneValide(this.patient.telephone) ? '' : MESSAGE_TELEPHONE,
        email: emailValide(this.patient.email) ? '' : MESSAGE_EMAIL,
        contactUrgenceTelephone: telephoneValide(this.patient.contactUrgenceTelephone) ? '' : MESSAGE_TELEPHONE,
        responsables: lignesValides(this.responsables) ? '' : 'Un téléphone de responsable est invalide.',
      };
      return !Object.values(this.error).some(err => err !== '');
    },

    async imprimerNotice() {
      await imprimer(documentNotice(await configClinique()));
    },

    async savePatient() {
      if (!this.validateForm() || this.isLoading) return;
      try {
        this.isLoading = true;
        const payload = {
          ...this.patient,
          dateNaissance: this.patient.dateNaissance || null,
          createdBy: this.currentUser.id,
          responsables: lignesPourServeur(this.responsables),
        };
        const response = await api.createItem('patients', payload);

        const newPatientId = response.data.id || response.data.data?.id;

        if (this.estAssure && this.$store.getters.hasModule('ASSURANCES')) {
          notifier('Patient enregistré : saisissez maintenant son contrat d\'assurance.', 'success');
          this.$router.push({ name: 'assurances_patient', params: { id: newPatientId }, query: { nouveau: 1 } });
        } else {
          this.$router.push({
            name: 'information_personnelle',
            params: { id: newPatientId }
          });
        }

      } catch (error) {
        notifier(messageErreur(error, "Le patient n'a pas pu être enregistré."));
      } finally {
        this.isLoading = false;
      }
    },

    cancel() {
      this.$router.push({ name: 'patients' });
    }
  }
}
</script>

<style scoped>
.header-clinique {
  padding: 15px 20px;
  background-color: #e9f5ff;
  border-radius: 12px;
  margin-bottom: 20px;
}

.card-clinique {
  background: #f8fafd;
  border-radius: 12px;
  border: none;
}

.form-control-clinique,
.form-select-clinique {
  border-radius: 10px;
  border: 1px solid #d1e3f0;
  padding: 10px 14px;
  transition: all 0.3s ease;
}
.form-control-clinique:focus,
.form-select-clinique:focus {
  box-shadow: 0 0 6px #0d6efd33;
  border-color: #0d6efd;
}

.btn-clinique {
  background: linear-gradient(90deg, #0d6efd, #6610f2);
  border: none;
  color: #fff;
  border-radius: 10px;
  transition: all 0.3s ease;
}
.btn-clinique:hover {
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  transform: translateY(-2px);
}
</style>
